import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export function useCreateReport() {
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({
      reportedUserId,
      reportedPostId,
      reason,
      details,
    }: {
      reportedUserId?: string;
      reportedPostId?: string;
      reason: string;
      details?: string;
    }) => {
      if (!user?.id) throw new Error('User not authenticated');

      const { error } = await supabase.from('reports').insert({
        reporter_id: user.id,
        reported_user_id: reportedUserId || null,
        reported_post_id: reportedPostId || null,
        reason,
        details: details || null,
      });

      if (error) throw error;
    },
  });
}

export interface ReportWithDetails {
  id: string;
  reporter_id: string;
  reported_user_id: string | null;
  reported_post_id: string | null;
  reason: string;
  details: string | null;
  status: string;
  reviewed_by: string | null;
  reviewed_at: string | null;
  created_at: string;
  reporter_profile?: { full_name: string; avatar_url: string | null } | null;
  reported_user_profile?: { full_name: string; avatar_url: string | null } | null;
  reported_post?: { content: string; user_id: string } | null;
}

export function useAdminReports() {
  return useQuery({
    queryKey: ['admin-reports'],
    queryFn: async (): Promise<ReportWithDetails[]> => {
      const { data: reports, error } = await supabase
        .from('reports')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      if (!reports || reports.length === 0) return [];

      // Gather unique user IDs and post IDs
      const reporterIds = [...new Set(reports.map((r) => r.reporter_id))];
      const reportedUserIds = [...new Set(reports.filter((r) => r.reported_user_id).map((r) => r.reported_user_id!))];
      const allUserIds = [...new Set([...reporterIds, ...reportedUserIds])];
      const postIds = [...new Set(reports.filter((r) => r.reported_post_id).map((r) => r.reported_post_id!))];

      // Fetch profiles
      const { data: profiles } = await supabase
        .from('profiles')
        .select('user_id, full_name, avatar_url')
        .in('user_id', allUserIds);

      const profileMap = new Map(profiles?.map((p) => [p.user_id, p]));

      // Fetch posts
      let postMap = new Map<string, { content: string; user_id: string }>();
      if (postIds.length > 0) {
        const { data: posts } = await supabase
          .from('posts')
          .select('id, content, user_id')
          .in('id', postIds);

        postMap = new Map(posts?.map((p) => [p.id, p]));
      }

      return reports.map((report) => ({
        ...report,
        reporter_profile: profileMap.get(report.reporter_id) || null,
        reported_user_profile: report.reported_user_id
          ? profileMap.get(report.reported_user_id) || null
          : null,
        reported_post: report.reported_post_id
          ? postMap.get(report.reported_post_id) || null
          : null,
      }));
    },
  });
}

export function useUpdateReport() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({
      reportId,
      status,
    }: {
      reportId: string;
      status: 'reviewed' | 'dismissed';
    }) => {
      if (!user?.id) throw new Error('User not authenticated');

      const { error } = await supabase
        .from('reports')
        .update({
          status,
          reviewed_by: user.id,
          reviewed_at: new Date().toISOString(),
        })
        .eq('id', reportId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-reports'] });
    },
  });
}
